import {
    createUploadSession,
    getQrFileDownloadUrl,
    clearProcessedQrFiles,
    type QrUploadSession,
    type QrUploadFile
} from "@rapid/utils/api/qr";
import { RapidStateCreator } from "../types";

import * as Sentry from "@sentry/react";

// Define a simplified session info type based on what we need
interface UploadSessionInfo {
    id: string;
    files: QrUploadFile[];
    completed: boolean;
    expiresAt: number;
}

export interface QrUploadSliceState {
    // Current session state
    currentSession: QrUploadSession | null;
    sessionInfo: UploadSessionInfo | null;
    files: QrUploadFile[];
    currentSessionLoading: boolean;
    currentSessionError: string | null;
    currentWorkspaceUrl: string | null;

    // File processing state
    processedFileIds: Set<string>;
    processingFiles: boolean;

    // Getters
    getCurrentSession: () => QrUploadSession | null;
    getSessionFiles: () => QrUploadFile[];
    hasActiveSession: () => boolean;
    getProcessedFileIds: () => Set<string>;
    isProcessingFiles: () => boolean;

    // Actions
    ensureValidSession: (workspaceUrl: string) => Promise<void>;
    createSession: (workspaceUrl: string) => Promise<void>;
    initializeSession: (workspaceUrl: string) => void;
    clearSession: () => void;
    setCurrentSessionLoading: (currentSessionLoading: boolean) => void;
    setCurrentSessionError: (currentSessionError: string | null) => void;

    // File processing
    processQrFiles: (
        onFileProcessed: (file: File) => void,
        onAnalytics: (event: string, data: any) => void
    ) => Promise<void>;
    markFileAsProcessed: (fileId: string) => void;

    // Real-time sync
    startListeningToSession: (id: string) => void;
    stopListeningToSession: () => void;

    // Persistence
    loadSessionFromStorage: (workspaceUrl: string) => void;
    saveSessionToStorage: (session: QrUploadSession, workspaceUrl: string) => void;
    clearSessionFromStorage: (workspaceUrl: string) => void;
}

const QR_SESSION_STORAGE_KEY_PREFIX = "qr_upload_session";
const SESSION_RENEWAL_BUFFER_MINUTES = 5; // Renew 5 minutes before expiry
const SESSION_CACHE_BUFFER_MINUTES = 1; // Cache until 1 minute before expiry

// Helper to get workspace-specific storage key
const getSessionStorageKey = (workspaceUrl: string) =>
    `${QR_SESSION_STORAGE_KEY_PREFIX}_${workspaceUrl}`;

// Helper to check if session needs renewal (5 minutes before expiry)
const sessionNeedsRenewal = (session: QrUploadSession): boolean => {
    const renewalTime = session.expiresAt - SESSION_RENEWAL_BUFFER_MINUTES * 60 * 1000;
    return Date.now() >= renewalTime;
};

// Helper to check if session is expired
const sessionIsExpired = (session: QrUploadSession): boolean => {
    return Date.now() >= session.expiresAt;
};

// Helper to check if cached session should be invalidated (1 minute before expiry)
const sessionCacheExpired = (session: QrUploadSession): boolean => {
    const cacheExpiryTime = session.expiresAt - SESSION_CACHE_BUFFER_MINUTES * 60 * 1000;
    return Date.now() >= cacheExpiryTime;
};

export const createQrUploadSlice: RapidStateCreator<QrUploadSliceState> = (set, get) => ({
    // Initial state
    currentSession: null as QrUploadSession | null,
    sessionInfo: null as UploadSessionInfo | null,
    files: [] as QrUploadFile[],
    currentSessionLoading: false,
    currentSessionError: null,
    currentWorkspaceUrl: null as string | null,

    // File processing state
    processedFileIds: new Set<string>(),
    processingFiles: false,

    // Private state for real-time sync
    _currentSessionUnsubscribe: null as (() => void) | null,
    _currentListeningSessionId: null as string | null,

    // Getters
    getCurrentSession: () => get().currentSession,
    getSessionFiles: () => get().files,
    hasActiveSession: () => !!get().currentSession,
    getProcessedFileIds: () => get().processedFileIds,
    isProcessingFiles: () => get().processingFiles,

    // Actions
    ensureValidSession: async (workspaceUrl: string) => {
        const state = get();

        // If workspace changed, clear current session
        if (state.currentWorkspaceUrl && state.currentWorkspaceUrl !== workspaceUrl) {
            get().clearSession();
        }

        // Always set the current workspace URL
        if (state.currentWorkspaceUrl !== workspaceUrl) {
            set({ currentWorkspaceUrl: workspaceUrl }, false, "ensureValidSession:setWorkspace");
        }

        // Try to load existing session from storage if we don't have one in memory
        let currentSession = get().currentSession;
        if (!currentSession) {
            get().loadSessionFromStorage(workspaceUrl);
            currentSession = get().currentSession;
        }

        // Check if we need to create or renew session
        if (!currentSession) {
            await get().createSession(workspaceUrl);
        } else if (sessionIsExpired(currentSession)) {
            await get().createSession(workspaceUrl);
        } else if (sessionCacheExpired(currentSession)) {
            await get().createSession(workspaceUrl);
        } else if (sessionNeedsRenewal(currentSession)) {
            await get().createSession(workspaceUrl);
        } else {
            // Ensure we're listening to the current session
            if (state._currentListeningSessionId !== currentSession.id) {
                get().startListeningToSession(currentSession.id);
            }
        }
    },

    createSession: async (workspaceUrl: string) => {
        try {
            set(
                { currentSessionLoading: true, currentSessionError: null },
                false,
                "createSession:start"
            );

            const response = await createUploadSession(workspaceUrl);
            const sessionData = response.data;

            // Save to localStorage and state
            get().saveSessionToStorage(sessionData, workspaceUrl);
            set(
                {
                    currentSession: sessionData,
                    currentWorkspaceUrl: workspaceUrl,
                    currentSessionLoading: false,
                    currentSessionError: null,
                    processedFileIds: new Set<string>() // Reset processed files for new session
                },
                false,
                "createSession:success"
            );

            // Start listening to the session
            get().startListeningToSession(sessionData.id);
        } catch (error) {
            console.error("Failed to create QR session:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Failed to create session";
            set(
                { currentSessionError: errorMessage, currentSessionLoading: false },
                false,
                "createSession:error"
            );
        }
    },

    clearSession: () => {
        const state = get();
        get().stopListeningToSession();
        if (state.currentWorkspaceUrl) {
            get().clearSessionFromStorage(state.currentWorkspaceUrl);
        }
        set(
            {
                currentSession: null,
                sessionInfo: null,
                files: [],
                currentSessionError: null,
                processedFileIds: new Set<string>(),
                processingFiles: false,
                currentWorkspaceUrl: null
            },
            false,
            "clearSession"
        );
    },

    initializeSession: (workspaceUrl: string) => {
        const state = get();

        // Only initialize if we don't have a session and aren't loading
        if (!state.currentSession && !state.currentSessionLoading) {
            // console.log("📱 Initializing session from storage for workspace:", workspaceUrl);
            get().loadSessionFromStorage(workspaceUrl);

            // Set workspace URL if not set
            if (state.currentWorkspaceUrl !== workspaceUrl) {
                set({ currentWorkspaceUrl: workspaceUrl }, false, "initializeSession:setWorkspace");
            }
        }
    },

    setCurrentSessionLoading: (currentSessionLoading: boolean) =>
        set({ currentSessionLoading }, false, "setCurrentSessionLoading"),
    setCurrentSessionError: (currentSessionError: string | null) =>
        set({ currentSessionError }, false, "setCurrentSessionError"),

    // File processing actions
    processQrFiles: async (
        onFileProcessed: (file: File) => void,
        onAnalytics: (event: string, data: any) => void
    ) => {
        const state = get();
        const { files, processedFileIds, currentSession, processingFiles } = state;

        if (!files || files.length === 0 || !currentSession) return;

        // Prevent concurrent processing
        if (processingFiles) {
            // console.log("📱 Already processing QR files, skipping...");
            return;
        }

        const newFiles = files.filter((file) => !processedFileIds.has(file.fileId));
        if (newFiles.length === 0) return;

        set({ processingFiles: true }, false, "processQrFiles:start");
        // console.log(`📱 Processing ${newFiles.length} new QR uploaded files:`, newFiles);

        const successfullyProcessedFileIds: string[] = [];

        for (const qrFile of newFiles) {
            try {
                // Double-check if file was processed while we were processing others
                if (get().processedFileIds.has(qrFile.fileId)) {
                    // console.log(`📱 File ${qrFile.fileName} already processed, skipping`);
                    continue;
                }

                // console.log(`📥 Processing QR file: ${qrFile.fileName}`);

                // Mark as processed immediately to prevent duplicate processing
                get().markFileAsProcessed(qrFile.fileId);

                // Get signed download URL from backend with retry mechanism
                let urlResponse;
                let downloadUrl;
                const maxRetries = 5;
                const timeoutMs = 2000;

                for (let attempt = 1; attempt <= maxRetries; attempt++) {
                    try {
                        // console.log(
                        //     `📥 Attempting to get download URL (attempt ${attempt}/${maxRetries}): ${qrFile.fileName}`
                        // );

                        // Create timeout promise
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error("Timeout")), timeoutMs)
                        );

                        // Race the API call against the timeout
                        urlResponse = await Promise.race([
                            getQrFileDownloadUrl(currentSession.id, qrFile.fileId),
                            timeoutPromise
                        ]);

                        downloadUrl = urlResponse.data.downloadUrl;
                        // console.log(
                        //     `✅ Got download URL on attempt ${attempt}: ${qrFile.fileName}`
                        // );
                        break;
                    } catch (error: any) {
                        console.warn(
                            `⚠️ Attempt ${attempt}/${maxRetries} failed for ${qrFile.fileName}:`,
                            error.message
                        );

                        if (attempt === maxRetries) {
                            console.error(
                                `❌ All ${maxRetries} attempts failed for ${qrFile.fileName}, skipping file`
                            );
                            continue; // Skip to next file instead of throwing
                        }

                        // Wait a bit before retrying (exponential backoff)
                        await new Promise((resolve) => setTimeout(resolve, attempt * 500));
                    }
                }

                // Skip this file if we couldn't get the download URL
                if (!downloadUrl) {
                    // console.log(
                    //     `⏭️ Skipping ${qrFile.fileName} - could not get download URL after ${maxRetries} attempts`
                    // );
                    continue;
                }

                // console.log(`📥 Downloading QR file from signed URL: ${qrFile.fileName}`);

                // Download the file using the signed URL
                const response = await fetch(downloadUrl);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                // Convert to File object
                const blob = await response.blob();
                const downloadedFile = new File([blob], qrFile.fileName, {
                    type: qrFile.mimeType || blob.type
                });

                // console.log(
                //     `📤 QR file ready for upload flow: ${qrFile.fileName} (${downloadedFile.size} bytes)`
                // );

                // Call the callback to process the file
                onFileProcessed(downloadedFile);

                // Track analytics
                onAnalytics("FILE_UPLOADER_CLICKED", { source: "qr_code" });

                // Add to list of successfully processed files
                successfullyProcessedFileIds.push(qrFile.fileId);
            } catch (error) {
                console.error(`❌ Failed to process QR file ${qrFile.fileName}:`, error);
                set(
                    {
                        currentSessionError: `Failed to process file from QR upload: ${qrFile.fileName}`,
                        processingFiles: false
                    },
                    false,
                    "processQrFiles:error"
                );
                return; // Stop processing on error
            }
        }

        // Clear successfully processed files from the QR session
        if (successfullyProcessedFileIds.length > 0) {
            try {
                // console.log(
                //     `🧹 Clearing ${successfullyProcessedFileIds.length} processed files from QR session`
                // );
                await clearProcessedQrFiles(currentSession.id, successfullyProcessedFileIds);
                // console.log(`✅ Successfully cleared processed files from QR session`);
            } catch (error) {
                console.error("❌ Failed to clear processed files from QR session:", error);
                // Don't fail the entire operation if clearing fails - files are already processed
            }
        }

        set({ processingFiles: false }, false, "processQrFiles:complete");
    },

    markFileAsProcessed: (fileId: string) => {
        const currentProcessed = get().processedFileIds;
        const newProcessed = new Set(currentProcessed);
        newProcessed.add(fileId);
        set({ processedFileIds: newProcessed }, false, "markFileAsProcessed");
    },

    // Real-time sync
    startListeningToSession: (id: string) => {
        // Don't start listening if already listening to the same session
        if (get()._currentListeningSessionId === id) {
            return;
        }

        // Stop any existing listener
        get().stopListeningToSession();

        try {
            const database = (get() as any)._database;

            // Start listening to the QR upload session document
            const unsubscribe = database.onSnapshot(
                "qr_upload_sessions",
                id,
                (snapshot: { exists(): boolean; data(): any; id: string }) => {
                    if (snapshot.exists()) {
                        const sessionData = snapshot.data();

                        const newFiles = sessionData.files || [];
                        set(
                            {
                                sessionInfo: {
                                    id: sessionData.id,
                                    files: newFiles,
                                    completed: sessionData.completed || false,
                                    expiresAt: sessionData.expiresAt
                                },
                                files: newFiles,
                                currentSessionError: null
                            },
                            false,
                            "startListeningToSession:update"
                        );
                    } else {
                        set(
                            {
                                sessionInfo: null,
                                files: [],
                                currentSessionError: "Session not found or expired"
                            },
                            false,
                            "startListeningToSession:notFound"
                        );
                    }
                },
                (error: Error) => {
                    console.error("Error listening to QR session:", error);
                    set(
                        {
                            currentSessionError:
                                error.message || "Failed to listen to session updates"
                        },
                        false,
                        "startListeningToSession:error"
                    );
                }
            );

            // Store the unsubscribe function and current listening ID
            set(
                { _currentSessionUnsubscribe: unsubscribe, _currentListeningSessionId: id },
                false,
                "startListeningToSession:start"
            );
        } catch (error) {
            console.error("Error setting up QR session listener:", error);
            set(
                {
                    currentSessionError:
                        error instanceof Error ? error.message : "Failed to set up session listener"
                },
                false,
                "startListeningToSession:setupError"
            );
        }
    },

    stopListeningToSession: () => {
        const unsubscribe = get()._currentSessionUnsubscribe;
        if (unsubscribe) {
            // console.log("📱 Stopping QR session listener");
            try {
                // Defensive cleanup - catch any errors during unsubscribe
                unsubscribe();
            } catch (error) {
                console.warn("Error during QR session listener cleanup:", error);
                Sentry.captureException(error, {
                    extra: { context: "qrUploadSlice.stopListeningToSession" }
                });
            } finally {
                // Always clear the reference even if unsubscribe failed
                set(
                    { _currentSessionUnsubscribe: null, _currentListeningSessionId: null },
                    false,
                    "stopListeningToSession"
                );
            }
        }
    },

    // Persistence
    loadSessionFromStorage: (workspaceUrl: string) => {
        try {
            const storageKey = getSessionStorageKey(workspaceUrl);
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                const session = JSON.parse(stored) as QrUploadSession;

                // Check if cached session is still valid (not expired and cache not expired)
                if (!sessionIsExpired(session) && !sessionCacheExpired(session)) {
                    // console.log(
                    //     "📱 Loaded valid QR session from storage:",
                    //     session.id,
                    //     `(expires in ${Math.round((session.expiresAt - Date.now()) / 60000)} minutes)`
                    // );
                    set(
                        { currentSession: session, currentWorkspaceUrl: workspaceUrl },
                        false,
                        "loadSessionFromStorage"
                    );

                    // Start listening to the restored session
                    get().startListeningToSession(session.id);
                } else {
                    const reason = sessionIsExpired(session)
                        ? "expired"
                        : "cache expired (1min buffer)";
                    // console.log(`📱 Stored QR session ${reason}, clearing`);
                    get().clearSessionFromStorage(workspaceUrl);
                }
            }
        } catch (error) {
            console.error("Failed to load QR session from storage:", error);
            get().clearSessionFromStorage(workspaceUrl);
        }
    },

    saveSessionToStorage: (session: QrUploadSession, workspaceUrl: string) => {
        try {
            const storageKey = getSessionStorageKey(workspaceUrl);
            localStorage.setItem(storageKey, JSON.stringify(session));
            // console.log(
            //     "📱 Saved QR session to storage:",
            //     session.id,
            //     "for workspace:",
            //     workspaceUrl
            // );
        } catch (error) {
            console.error("Failed to save QR session to storage:", error);
        }
    },

    clearSessionFromStorage: (workspaceUrl: string) => {
        try {
            const storageKey = getSessionStorageKey(workspaceUrl);
            localStorage.removeItem(storageKey);
            // console.log("📱 Cleared QR session from storage for workspace:", workspaceUrl);
        } catch (error) {
            console.error("Failed to clear QR session from storage:", error);
        }
    }
});
